/**
 * The chat transport (SUP-256): the thread feed down the /feed socket,
 * command POSTs up the same gated path. Settled state caches in the local
 * store, so reopening paints from cache and joins with `?v=<cursor>` — a
 * hit costs one `hello`. Down is state, up is requests-with-outcomes:
 * refusals come back as the POST response, which a feed can't express.
 */
import {
  parsePartialJson,
  type ChatStatus,
  type FileUIPart,
  type SourceDocumentUIPart,
  type TextUIPart,
  type UIMessage,
} from "ai";
import { useCallback, useEffect, useRef, useState } from "react";

import { SYNC_PONG } from "../sync-keepalive";
import {
  cachedFeedState,
  emptyFeedState,
  feedView,
  mintUlid,
  reduceFeed,
  type FeedFrame,
  type FeedState,
  type StreamingPart,
  type ThreadPhase,
} from "../thread-feed";
import { SocketResumeMonitor } from "./socket-resume";

/**
 * A message leaving the composer, as the queued/pending bubbles render it.
 * Structurally the chat shell's `SentMessage` (`forest/chat/types`) — declared
 * here too so the transport has no UI-package edge; hosts pass either.
 */
export interface SentMessage {
  text: string;
  files: FileUIPart[];
  contextParts?: (TextUIPart | SourceDocumentUIPart)[];
  hidden?: boolean;
}

function agentPath(agentClass: string, name: string, suffix: string): string {
  return `/api/agents/${agentClass}/${name}/${suffix}`;
}

/** Message ids are ULIDs — identity is order. */
export function mintId(): string {
  return mintUlid();
}

export interface ChatCommands {
  sendMessage(requestId: string, message: UIMessage): Promise<void>;
  stop(): Promise<void>;
  approveTool(toolCallId: string, approved: boolean): Promise<void>;
  /** Failures ride error-shaped outputs so auto-continuation still fires. */
  submitToolResult(toolCallId: string, output: unknown): Promise<void>;
  clear(): Promise<void>;
}

/** A definitive refusal, never retried; ambiguous network throws retry
 *  under the same requestId and dedupe on the ledger. */
export class CommandRefused extends Error {}

export interface ChatCommandOptions {
  agentClass?: string;
  /** Awaited before every POST — the host's "this thread's create has
   *  landed" gate (hearth passes `threadReady`). */
  ready?: (name: string) => Promise<void>;
  /** Injectable `fetch`, defaulting to the global. */
  fetch?: typeof fetch;
}

export function chatCommands(
  name: string,
  opts: ChatCommandOptions = {},
): ChatCommands {
  const agentClass = opts.agentClass ?? "chat-host";
  const fetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const post = async (suffix: string, body: unknown): Promise<unknown> => {
    // The host writes back to a row the create POST may still be landing,
    // so every command waits for it (a no-op for any other thread).
    await opts.ready?.(name);
    const res = await fetch(agentPath(agentClass, name, suffix), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const parsed = (await res.json().catch(() => null)) as {
      error?: string;
    } | null;
    if (!res.ok) {
      throw new CommandRefused(
        parsed?.error ?? `${suffix} failed (${res.status})`,
      );
    }
    return parsed;
  };
  return {
    sendMessage: async (requestId, message) =>
      void (await post("message", { requestId, messages: [message] })),
    stop: async () => void (await post("stop", {})),
    approveTool: async (toolCallId, approved) =>
      void (await post("tool-approval", { toolCallId, approved })),
    submitToolResult: async (toolCallId, output) =>
      void (await post("tool-result", { toolCallId, output })),
    clear: async () => void (await post("clear", {})),
  };
}

export interface ChatFeedView<M extends UIMessage = UIMessage> {
  messages: M[];
  phase: ThreadPhase;
}

export interface ChatFeedState<M extends UIMessage = UIMessage> {
  /** Null until the feed has synced in (or painted from cache). */
  view: ChatFeedView<M> | null;
  connected: boolean;
}

/** Settled-state cache keyed by thread name — paint-before-connect and the
 *  `?v=` join cursor. Absent, every open joins cold with a snapshot. */
export interface ChatFeedCache {
  load(name: string): Promise<Uint8Array | undefined>;
  save(name: string, bytes: Uint8Array): Promise<void>;
}

/** No hello inside this window after open ⇒ the join was lost (the DO idled
 *  before its write chain ran); close and let the backoff re-join. */
const HELLO_TIMEOUT_MS = 8_000;

interface CachedFeed {
  v: number;
  phase: ThreadPhase;
  messages: UIMessage[];
}

/** Parse a live tool part's raw `inputText` into `input` for display. */
async function withParsedToolInputs<M extends UIMessage>(
  view: ChatFeedView<M>,
): Promise<ChatFeedView<M>> {
  let touched = false;
  const messages = await Promise.all(
    view.messages.map(async (message) => {
      const needs = (message.parts as StreamingPart[]).some(
        (p) =>
          (p as { state?: string }).state === "input-streaming" &&
          typeof p.inputText === "string",
      );
      if (!needs) return message;
      touched = true;
      const parts = await Promise.all(
        (message.parts as StreamingPart[]).map(async (part) => {
          const p = part as { state?: string; inputText?: string };
          if (p.state !== "input-streaming" || !p.inputText) return part;
          const { value } = await parsePartialJson(p.inputText);
          if (value === undefined || value === null) return part;
          return { ...part, input: value } as typeof part;
        }),
      );
      return { ...message, parts } as typeof message;
    }),
  );
  return touched ? { ...view, messages: messages as M[] } : view;
}

/** Sync the thread's feed. The thread name is the address; a version
 *  mismatch on join simply yields a snapshot. `enabled: false` renders the
 *  no-thread state (a draft chat whose row doesn't exist yet) without
 *  opening a socket to a name that means nothing. */
export function useChatFeed<M extends UIMessage = UIMessage>(
  agentClass: string,
  name: string,
  opts: {
    cache?: ChatFeedCache;
    enabled?: boolean;
  } = {},
): ChatFeedState<M> {
  const [view, setView] = useState<ChatFeedView<M> | null>(null);
  const [connected, setConnected] = useState(false);
  const { cache, enabled = true } = opts;

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let ws: WebSocket | null = null;
    let attempt = 0;
    let state: FeedState = emptyFeedState();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let helloTimer: ReturnType<typeof setTimeout> | null = null;
    let persistTimer: ReturnType<typeof setTimeout> | null = null;
    let renderEpoch = 0;
    // The monitor probes `holder.socket` on tab-resume/online events.
    const holder: { socket?: WebSocket } = {};
    const monitor = new SocketResumeMonitor(holder);
    monitor.start();

    const render = (next: FeedState) => {
      state = next;
      const base = feedView(state);
      if (base === null) return;
      const epoch = ++renderEpoch;
      setView(base as ChatFeedView<M>);
      // Live tool inputs parse async; re-render only if still current.
      void withParsedToolInputs(base as ChatFeedView<M>).then((parsed) => {
        if (!disposed && epoch === renderEpoch && parsed !== base) {
          setView(parsed);
        }
      });
    };

    const persistSettled = () => {
      if (cache === undefined || persistTimer !== null) return;
      persistTimer = setTimeout(() => {
        persistTimer = null;
        const settled = state;
        if (settled.v === null) return;
        const payload: CachedFeed = {
          v: settled.v,
          phase: settled.phase,
          messages: [...settled.messages.values()],
        };
        void cache.save(
          name,
          new TextEncoder().encode(JSON.stringify(payload)),
        );
      }, 400);
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) return;
      const delay = Math.min(15_000, 1000 * 2 ** Math.min(attempt, 4));
      attempt++;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    };

    const clearHelloTimer = () => {
      if (helloTimer !== null) {
        clearTimeout(helloTimer);
        helloTimer = null;
      }
    };

    const connect = async () => {
      if (disposed) return;
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const params = new URLSearchParams();
      if (state.v !== null) params.set("v", String(state.v));
      const query = params.size > 0 ? `?${params.toString()}` : "";
      const socket = new WebSocket(
        `${wsProtocol}//${window.location.host}${agentPath(agentClass, name, "feed")}${query}`,
      );
      ws = socket;
      holder.socket = socket;
      socket.onopen = () => {
        if (disposed || socket !== ws) return;
        setConnected(true);
        clearHelloTimer();
        helloTimer = setTimeout(() => {
          if (socket === ws) socket.close();
        }, HELLO_TIMEOUT_MS);
      };
      socket.onmessage = (event) => {
        if (disposed || socket !== ws || typeof event.data !== "string") return;
        if (event.data === SYNC_PONG) return;
        let frame: FeedFrame;
        try {
          frame = JSON.parse(event.data) as FeedFrame;
        } catch {
          return;
        }
        if (frame.t === "hello") {
          clearHelloTimer();
          attempt = 0;
        }
        const before = state.v;
        const { state: next, resync } = reduceFeed(state, frame);
        if (resync) {
          // Gap, duplicate, or a stream frame without its base: the join
          // path IS the resync path.
          socket.close();
          return;
        }
        render(next);
        if (next.v !== before) persistSettled();
      };
      socket.onclose = () => {
        if (socket !== ws) return;
        clearHelloTimer();
        if (!disposed) {
          setConnected(false);
          scheduleReconnect();
        }
      };
      socket.onerror = () => {
        if (socket === ws) socket.close();
      };
    };

    // Paint from the cached settled state, then join with its version.
    void (async () => {
      try {
        const bytes = disposed ? undefined : await cache?.load(name);
        if (bytes && !disposed) {
          const cached = JSON.parse(
            new TextDecoder().decode(bytes),
          ) as CachedFeed;
          render(cachedFeedState(cached.v, cached.phase, cached.messages));
        }
      } catch {
        // A corrupt cache is replaced by the join's snapshot.
      }
      if (!disposed) void connect();
    })();

    return () => {
      disposed = true;
      renderEpoch++;
      setView(null);
      setConnected(false);
      monitor.stop();
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      if (persistTimer !== null) clearTimeout(persistTimer);
      clearHelloTimer();
      const socket = ws;
      ws = null;
      try {
        socket?.close();
      } catch {
        // Torn-down is fine.
      }
    };
  }, [agentClass, name, cache, enabled]);

  return { view, connected };
}

/** Drop pending sends the feed has echoed back (matched by id). */
export function pendingAfterEcho<T extends { id: string }>(
  pending: T[],
  messages: { id: string }[],
): T[] {
  const ids = new Set(messages.map((m) => m.id));
  return pending.filter((p) => !ids.has(p.id));
}

/** The composer's hidden workspace-context block opens with this tag. The
 *  pair is owned by `forest/chat/workspace-context.ts`; the open tag is
 *  mirrored here because the transport must not depend on the UI package. */
const CONTEXT_OPEN_TAG = "<workspace_context>";

/** A waiting message rendered through the queued-bubble UI. */
function sentView(m: UIMessage): SentMessage {
  const parts = m.parts as {
    type: string;
    text?: string;
    metadata?: { hidden?: boolean };
  }[];
  return {
    text: parts
      .filter(
        (p) =>
          p.type === "text" &&
          p.metadata?.hidden !== true &&
          // Context snapshots are model-facing: a queued bubble must not
          // expose a file snapshot or sketch dump as the member's words.
          !(p.text ?? "").trimStart().startsWith(CONTEXT_OPEN_TAG),
      )
      .map((p) => p.text ?? "")
      .join("\n"),
    files: parts.filter((p) => p.type === "file") as SentMessage["files"],
    hidden: (m.metadata as { hidden?: boolean } | undefined)?.hidden === true,
  };
}

/**
 * While a turn runs, trailing user messages with no assistant reply after
 * them are WAITING — accepted but not yet driven — and render as queued
 * bubbles below the turn's activity. Derived, not tracked: starting their
 * turn mints an assistant row after them, which ends the wait.
 */
export function splitWaitingSends<M extends UIMessage>(
  messages: M[],
  phase: ThreadPhase,
): { transcript: M[]; waiting: SentMessage[] } {
  if (phase === "idle") return { transcript: messages, waiting: [] };
  let cut = messages.length;
  while (cut > 0 && messages[cut - 1]!.role === "user") cut--;
  if (cut === messages.length) return { transcript: messages, waiting: [] };
  return {
    transcript: messages.slice(0, cut),
    waiting: messages.slice(cut).map(sentView),
  };
}

export interface ChatSend {
  /** Sends the feed hasn't echoed yet — render as dimmed bubbles. */
  pending: SentMessage[];
  /** One status rule: error > live turn > pending send > ready. */
  status: ChatStatus;
  error: Error | undefined;
  send: (message: UIMessage, sent: SentMessage) => void;
  /** Run any command with the shared retry/report contract; rethrows so
   *  callers (the approval card) can reset their own state. */
  perform: (op: () => Promise<void>) => Promise<void>;
  /** Surface a non-send command failure the same way. */
  report: (err: unknown) => void;
}

/** The one send/status contract for every chat surface. No client queue:
 *  the server's ledger serializes turns, so sends POST immediately. */
export function useChatSend<M extends UIMessage>(
  commands: ChatCommands,
  view: ChatFeedView<M> | null,
): ChatSend {
  const [pending, setPending] = useState<{ id: string; sent: SentMessage }[]>(
    [],
  );
  const [error, setError] = useState<Error | undefined>();

  // Send state is keyed to the transport target: when `commands` points at a
  // different thread (a host that switches chats without remounting, like
  // bear's pane), the previous thread's pending bubbles and error must not
  // carry over — its feed can never echo them away.
  const boundTo = useRef(commands);
  useEffect(() => {
    if (boundTo.current === commands) return;
    boundTo.current = commands;
    setPending([]);
    setError(undefined);
  }, [commands]);

  const messages = view?.messages;
  useEffect(() => {
    if (!messages || pending.length === 0) return;
    const next = pendingAfterEcho(pending, messages);
    if (next.length !== pending.length) setPending(next);
  }, [messages, pending]);

  const report = useCallback((err: unknown) => {
    setError(err instanceof Error ? err : new Error(String(err)));
  }, []);

  /** The ONE command contract: retry ambiguous failures (the ledger's
   *  idempotency absorbs a landed-but-unacked attempt), report and stop on
   *  a definitive refusal. Rethrows for caller-side state resets. */
  const perform = useCallback(
    async (op: () => Promise<void>) => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await op();
        } catch (err) {
          if (err instanceof CommandRefused || attempt >= 2) {
            report(err);
            throw err;
          }
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    },
    [report],
  );

  const send = useCallback(
    (message: UIMessage, sent: SentMessage) => {
      setError(undefined);
      setPending((q) => [...q, { id: message.id, sent }]);
      // ONE requestId across attempts — retries dedupe onto `web:<id>`.
      const requestId = mintId();
      perform(() => commands.sendMessage(requestId, message)).catch(() => {
        setPending((q) => q.filter((p) => p.id !== message.id));
      });
    },
    [commands, perform],
  );

  const phase = view?.phase ?? "idle";

  // Feed progress supersedes a stale command error: any phase transition
  // (a continuation starting after an approval retry, a turn settling after
  // a failed stop) clears it.
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (prevPhase.current !== phase) setError(undefined);
    prevPhase.current = phase;
  }, [phase]);
  const status: ChatStatus = error
    ? "error"
    : phase !== "idle"
      ? "streaming"
      : pending.length > 0
        ? "submitted"
        : "ready";

  return {
    pending: pending.map((p) => p.sent),
    status,
    error,
    send,
    perform,
    report,
  };
}
